import React, { useState } from "react";
import { Home, Menu, ClipboardPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";

interface SidebarProps {
  viewMode: "dashboard" | "patients" | "doctors";
  setViewMode: (mode: "dashboard" | "patients" | "doctors") => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  viewMode: "dashboard" | "patients" | "doctors";
  setViewMode: (mode: "dashboard" | "patients" | "doctors") => void;
  onToggleCollapse?: () => void;
}

// Mobile Sidebar Overlay
const MobileSidebar: React.FC<MobileSidebarProps> = ({
  isOpen,
  onClose,
  viewMode,
  setViewMode,
  onToggleCollapse,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
      <div className="relative h-full">
        <div
          className={`absolute left-0 top-0 h-full bg-white shadow-xl transition-all duration-300 w-64
          `}
        >
          <SidebarContent
            viewMode={viewMode}
            setViewMode={(mode) => {
              setViewMode(mode);
              onClose();
            }}
            isCollapsed={false}
            onToggleCollapse={onToggleCollapse}
          />
        </div>

        {/* Close area */}
        <div className="h-full ml-64" onClick={onClose}></div>
      </div>
    </div>
  );
};

// Mobile Header Component
export const MobileHeader: React.FC<{
  onMenuClick: () => void;
}> = ({ onMenuClick }) => {
  return (
    <div className="lg:hidden fixed top-1 left-4 py-2 z-50">
      <div className="flex items-center justify-between py-1">
        <button
          onClick={onMenuClick}
          className="bg-secondary/50 rounded-full p-2 mr-2"
          aria-label="Open navigation menu"
        >
          <Menu className="h-4 w-4" />
        </button>
        <div className="w-10" />
      </div>
    </div>
  );
};

// Sidebar Content (shared between mobile and desktop)
const SidebarContent: React.FC<SidebarProps> = ({
  viewMode,
  setViewMode,
  isCollapsed = false,
  onToggleCollapse,
}) => {

  const navigationItems = [
    { id: "dashboard", label: "Home", link: "/chat", icon: Home, disabled: false },
    { id: "patients", label: "Patients", link: "/patients", icon: ClipboardPlus , disabled: false },
    // { id: "doctors", label: "Doctors", link: "/doctors", icon: Stethoscope, disabled: true },
  ];

  return (
    <div className="bg-primary-foreground flex flex-col h-full">
      {/* Sidebar Header with Logo and Toggle */}
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          {/* Collapse Toggle Button - Only show on desktop */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hidden lg:flex"
            onClick={onToggleCollapse}
          >
            <Menu className={`h-4 w-4`} />
          </Button>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="px-3 py-4 flex-1">
        <ul className="space-y-2">
          {navigationItems.map((item) => (
            <li key={item.id}>
              <Link to={item.link}>
                <Button
                  onClick={() => setViewMode(item.id as any)}
                  disabled={item.disabled}
                  variant="ghost"
                  className={`w-full rounded-lg justify-start py-2.5 ${
                    isCollapsed ? "px-3" : "px-3"
                  } ${
                    viewMode === item.id
                      ? "bg-[#e6f5fb] text-primary hover:bg-[#d9f0fa] border border-[#bfe4f6] shadow-sm"
                      : "text-muted-foreground hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  <item.icon
                    className={`${
                      isCollapsed ? "h-5 w-5 mx-auto" : "h-5 w-5 mr-3"
                    } ${
                      viewMode === item.id ? "text-primary" : "text-muted-foreground"
                    }`}
                  />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </Button>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

// Main Sidebar Component
export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed = false,
  onToggleCollapse,
  // viewMode,
  setViewMode,
}) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();

  //viewMode from current path
  const getViewModeFromPath = (pathname: string): "dashboard" | "patients" | "doctors" => {
    if (pathname.includes('/patients')) return 'patients';
    if (pathname.includes('/doctors')) return 'doctors';
    if (pathname.includes('/patient-details')) return 'patients';
    return 'dashboard';
  };
  
  const viewMode = getViewModeFromPath(location.pathname);

  return (
    <>
      {/* Mobile Header - Always rendered but only visible on mobile */}
      <MobileHeader onMenuClick={() => setMobileSidebarOpen(true)} />

      {/* Mobile Sidebar Overlay */}
      <MobileSidebar
        isOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onToggleCollapse={onToggleCollapse}
      />

      {/* Desktop Sidebar - Only shown on desktop */}
      <div className="hidden lg:block fixed top-0 left-0 h-screen z-40">
        <div
          className={`border-r bg-primary-foreground h-full transition-all duration-300 ${
            isCollapsed ? "w-16" : "w-56"
          }`}
        >
          <SidebarContent
            viewMode={viewMode}
            setViewMode={setViewMode}
            isCollapsed={isCollapsed}
            onToggleCollapse={onToggleCollapse}
          />
        </div>
      </div>
    </>
  );
};
